namespace $.$$ {

	type $shm_echarts_instance = {
		setOption( option: any, opts?: any ): void
		resize(): void
		dispose(): void
		on( event: string, handler: ( next: any )=> void ): void
		dispatchAction( action: any ): void
		getModel(): any
	}

	export class $shm_echarts extends $.$shm_echarts {

		cursor_cached = ''
		brush_cached = false
		chart_current: $shm_echarts_instance | null = null
		chart_observer: ResizeObserver | null = null

		animation_duration_initial() {
			return this.$.$shm_echarts_settings_store.duration_initial_runtime()
		}

		animation_duration_update() {
			return this.$.$shm_echarts_settings_store.duration_update_runtime()
		}

		animation_easing() {
			return this.$.$shm_echarts_settings_store.easing_runtime()
		}

		grid_rect() {
			const grid = this.chart().getModel()?.getComponent( 'grid', 0 )?.coordinateSystem?.getRect?.()
			if( !grid ) return { x: 0, y: 0, width: 0, height: 0 }
			return {
				x: Number( grid.x ) || 0,
				y: Number( grid.y ) || 0,
				width: Number( grid.width ) || 0,
				height: Number( grid.height ) || 0,
			}
		}

		@ $mol_mem
		echarts() {
			return this.$.$mol_import.script( 'https://fastly.jsdelivr.net/npm/echarts@5/dist/echarts.min.js' ).echarts
		}

		@ $mol_mem
		chart() {
			const chart = this.echarts().init( this.dom_node(), null, {
				renderer: 'svg',
				useDirtyRect: false,
			} ) as $shm_echarts_instance

			const observer = new ResizeObserver( ()=> chart.resize() )
			observer.observe( this.dom_node() )
			this.chart_current = chart
			this.chart_observer = observer
			chart.on( 'click', next => this.click( next ) )
			chart.on( 'brushEnd', next => this.brush_end( next ) )
			chart.on( 'finished', ()=> {
				this.cursor_apply()
				this.finished({})
			} )

			return chart
		}

		override destructor() {
			this.chart_observer?.disconnect()
			this.chart_observer = null
			this.chart_current?.dispose()
			this.chart_current = null
			super.destructor()
		}

		lights() {
			return this.$.$mol_lights()
		}
		
		theme() {
			return this.lights() ? '$mol_theme_light' : '$mol_theme_dark'
		}

		auto() {
			return [
				... super.auto(),
				this.option_apply(),
			] as readonly any[]
		}

		@ $mol_mem
		option_apply() {
			this.lights()
			const option = this.option_themed()
			const brush = this.brush_cursor()
			this.cursor_cached = this.option_cursor_from( option )
			this.brush_cached = Boolean( brush )
			this.chart().setOption( option, {
				notMerge: true,
				lazyUpdate: false,
			} )
			if( brush ) this.chart().dispatchAction({
				type: 'takeGlobalCursor',
				key: 'brush',
				brushOption: brush,
			})
			this.cursor_apply()
			return null
		}

		option_cursor() {
			return this.option_cursor_from( this.option_themed() )
		}

		option_cursor_from( option: any ) {
			option ??= {}
			const series = ( Array.isArray( option.series ) ? option.series : [ option.series ] ) as any[]
			return series.find( item => item?.cursor )?.cursor ?? ''
		}

		cursor_apply() {
			const cursor = this.cursor_cached
			const brush = this.brush_cached
			if( !cursor && !brush ) return null

			const node = this.dom_node() as HTMLElement
			node.style.setProperty( 'cursor', brush ? 'crosshair' : '', 'important' )
			for( const child of node.querySelectorAll( 'svg, svg *' ) ) {
				;( child as HTMLElement ).style.removeProperty( 'cursor' )
			}
			for( const child of node.querySelectorAll( 'svg' ) ) {
				;( child as SVGElement ).style.setProperty( 'cursor', brush ? 'crosshair' : '', 'important' )
			}
			if( !cursor ) return null

			for( const child of node.querySelectorAll( 'path, rect, circle, polygon' ) ) {
				const element = child as HTMLElement
				const box = element.getBoundingClientRect()
				const style = getComputedStyle( element )
				if( box.width <= 0 || box.height <= 0 ) continue
				if( style.fill === 'none' || style.fill === 'transparent' ) continue
				element.style.setProperty( 'cursor', cursor, 'important' )
			}
			return null
		}
		
		option_themed() {
			const option = this.option() ?? {}
			const theme = this.chart_theme()
			const animation_duration_initial = this.animation_duration_initial()
			const animation_duration_update = this.animation_duration_update()
			const animation_easing = this.animation_easing()
			return {
				... option,
				animation: animation_duration_initial > 0 || animation_duration_update > 0,
				animationDuration: animation_duration_initial,
				animationDurationUpdate: animation_duration_update,
				animationEasing: animation_easing,
				animationEasingUpdate: animation_easing,
				textStyle: this.option_merge( { color: theme.text }, option.textStyle ),
				legend: this.option_merge_each( {
					textStyle: { color: theme.text },
					pageTextStyle: { color: theme.text },
					pageIconColor: theme.text,
					pageIconInactiveColor: theme.shade,
				}, option.legend ),
				xAxis: this.option_merge_each( this.axis_theme( theme ), option.xAxis ),
				yAxis: this.option_merge_each( this.axis_theme( theme ), option.yAxis ),
				series: this.option_merge_each( this.series_theme( theme ), option.series ),
				tooltip: this.option_merge( {
					backgroundColor: theme.card,
					borderColor: theme.line,
					textStyle: { color: theme.text },
					extraCssText: 'box-shadow: none;',
				}, option.tooltip ),
				dataZoom: this.option_merge_each( {
					textStyle: { color: theme.text },
					borderColor: theme.line,
					fillerColor: theme.hover,
					backgroundColor: theme.back,
					dataBackground: {
						lineStyle: { color: theme.shade },
						areaStyle: { color: theme.line },
					},
					selectedDataBackground: {
						lineStyle: { color: theme.text },
						areaStyle: { color: theme.hover },
					},
					handleStyle: {
						color: theme.card,
						borderColor: theme.text,
					},
					moveHandleStyle: {
						color: theme.text,
					},
					emphasis: {
						handleStyle: {
							color: theme.back,
							borderColor: theme.text,
						},
					},
				}, option.dataZoom ),
			}
		}
		
		chart_theme() {
			this.lights()
			const next = {
				back: 'var(--mol_theme_back)',
				card: 'var(--mol_theme_card)',
				text: 'var(--mol_theme_text)',
				shade: 'var(--mol_theme_shade)',
				line: 'var(--mol_theme_line)',
				hover: 'var(--mol_theme_hover)',
			}
			return next
		}
		
		axis_theme( theme: ReturnType< $shm_echarts['chart_theme'] > ) {
			return {
				nameTextStyle: { color: theme.text },
				axisLabel: { color: theme.shade },
				axisLine: { lineStyle: { color: theme.line } },
				axisTick: { lineStyle: { color: theme.line } },
				splitLine: { lineStyle: { color: theme.line } },
				splitArea: { areaStyle: { color: [ 'transparent', theme.hover ] } },
			}
		}
		
		series_theme( theme: ReturnType< $shm_echarts['chart_theme'] > ) {
			return {
				label: {
					color: theme.text,
					textBorderColor: theme.back,
					textBorderWidth: 2,
				},
				emphasis: {
					label: {
						color: theme.text,
						textBorderColor: theme.back,
						textBorderWidth: 2,
					},
				},
				labelLine: {
					lineStyle: { color: theme.line },
				},
			}
		}
		
		option_merge( base: any, patch: any ): any {
			if( !patch ) return base
			if( Array.isArray( base ) || Array.isArray( patch ) ) return patch
			if( typeof base !== 'object' || typeof patch !== 'object' ) return patch
			
			const next = { ... base }
			for( const key of Object.keys( patch ) ) next[ key ] = this.option_merge( base[ key ], patch[ key ] )
			return next
		}
		
		option_merge_each( base: any, patch: any ) {
			if( patch === undefined || patch === null ) return patch
			if( Array.isArray( patch ) ) return patch.map( item => this.option_merge( base, item ) )
			return this.option_merge( base, patch )
		}

	}

}
